import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  Banner,
  Spinner,
  BlockStack,
  InlineStack,
  Modal,
  Box,
  TextField,
  Tabs,
  Badge,
  EmptyState,
  ProgressBar,
} from "@shopify/polaris";
import { useShop } from "../context/ShopContext.jsx";
import { useI18n } from "../context/I18nContext.jsx";

// Extended garment-specific measurement keys — as many fields as possible for best advice
const GARMENT_KEYS = {
  colbert: ["chest", "waist", "shoulders", "armLength", "neck", "backLength", "frontLength", "hem", "sleeveWidth", "cuff"],
  gilet: ["chest", "waist", "shoulders", "backLength", "frontLength", "hem"],
  pantalon: ["waist", "hips", "inseam", "outseam", "thigh", "knee", "hemWidth", "frontRise", "backRise"],
  overhemd: ["neck", "chest", "waist", "armLength"],
  kinderpak: ["chest", "waist", "height"],
  schoenen: ["footLength"],
};

const ALL_MEASUREMENT_KEYS = [...new Set(Object.values(GARMENT_KEYS).flat())];

const GARMENT_LABELS = {
  colbert: "Colbert / Blazer",
  gilet: "Gilet / Waistcoat",
  pantalon: "Pantalon / Broek",
  overhemd: "Overhemd / Shirt",
  kinderpak: "Kinderpak / Boys",
  schoenen: "Schoenen / Shoes",
};

const GARMENT_TYPE_LIST = Object.keys(GARMENT_KEYS);

// Normaliseer elk chart-formaat naar een volledig edit-object met alle types
const toEditFormat = (raw) => {
  const empty = Object.fromEntries(GARMENT_TYPE_LIST.map(gt => [gt, []]));
  if (Array.isArray(raw)) {
    // Legacy flat array → put all entries in colbert (user can redistribute)
    return { ...empty, colbert: JSON.parse(JSON.stringify(raw)) };
  }
  if (raw && typeof raw === "object") {
    const out = { ...empty };
    for (const gt of GARMENT_TYPE_LIST) {
      if (Array.isArray(raw[gt])) out[gt] = JSON.parse(JSON.stringify(raw[gt]));
    }
    return out;
  }
  return empty;
};

// Empty measurements template per garment type
const emptyMeasurements = (garmentType) => {
  const m = {};
  for (const key of (GARMENT_KEYS[garmentType] || ALL_MEASUREMENT_KEYS)) m[key] = 0;
  return m;
};

export default function SizeChartPage() {
  const { connected, loading: shopLoading, apiFetch } = useShop();
  const { t } = useI18n();

  const MEASUREMENT_LABELS = useMemo(() => ({
    chest: t("sizeChart.chest") || "Borst",
    waist: t("sizeChart.waist") || "Taille",
    hips: t("sizeChart.hips") || "Heupen",
    shoulders: t("sizeChart.shoulders") || "Schouders",
    armLength: t("sizeChart.armLength") || "Mouwlengte",
    inseam: t("sizeChart.inseam") || "Binnenbeenlengte",
    neck: t("sizeChart.neck") || "Nek",
    backLength: "Ruglengte",
    frontLength: "Voorlengte",
    hem: "Zoom/Bodem",
    sleeveWidth: "Mouwwijdte",
    cuff: "Manchet",
    outseam: "Buitenbeenlengte",
    thigh: "Dij",
    knee: "Knie",
    hemWidth: "Pijpwijdte",
    frontRise: "Voorhoogte",
    backRise: "Achterhoogte",
    height: "Lichaamslengte",
    footLength: "Voetlengte",
  }), [t]);

  // Vendor data
  const [vendors, setVendors] = useState([]); // unique vendor names from products
  const [vendorCharts, setVendorCharts] = useState({}); // vendor -> sizeChart[]
  const [vendorSizeSystems, setVendorSizeSystems] = useState({}); // vendor -> { sizeSystem, displaySystem }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [activeVendor, setActiveVendor] = useState(null);
  const [editChart, setEditChart] = useState(toEditFormat(null));
  const [editSizeSystem, setEditSizeSystem] = useState("EU");
  const [editDisplaySystem, setEditDisplaySystem] = useState("EU");
  const [saving, setSaving] = useState(false);
  const [activeGarmentTab, setActiveGarmentTab] = useState(0); // index in GARMENT_TYPE_LIST

  // Import modal state
  const [importOpen, setImportOpen] = useState(false);
  const [importVendor, setImportVendor] = useState(null);
  const [importJson, setImportJson] = useState("");
  const [importing, setImporting] = useState(false);

  // URL scrape state
  const [scrapeOpen, setScrapeOpen] = useState(false);
  const [scrapeVendor, setScrapeVendor] = useState(null);
  const [scrapeUrl, setScrapeUrl] = useState("");
  const [scraping, setScraping] = useState(false);

  // Image scan state
  const [scanning, setScanning] = useState(null); // vendor name being scanned, or null
  const fileInputRef = useRef(null);

  // Load all unique vendors from products + existing vendor charts
  const loadData = useCallback(async () => {
    if (!connected) return;
    setLoading(true);
    try {
      // Fetch products to get unique vendors
      const productData = await apiFetch("/shopify-products?limit=250");
      const uniqueVendors = [...new Set(
        (productData.products || []).map(p => p.vendor).filter(Boolean)
      )].sort();
      setVendors(uniqueVendors);

      // Fetch existing vendor size charts
      const charts = await apiFetch("/product-fitting/vendors");
      const chartMap = {};
      const systemMap = {};
      for (const vc of charts) {
        chartMap[vc.vendor] = vc.sizeChart || [];
        systemMap[vc.vendor] = { sizeSystem: vc.sizeSystem || "EU", displaySystem: vc.displaySystem || "EU" };
      }
      setVendorCharts(chartMap);
      setVendorSizeSystems(systemMap);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [connected, apiFetch]);

  useEffect(() => {
    if (!shopLoading && connected) {
      loadData();
    } else if (!shopLoading && !connected) {
      setLoading(false);
    }
  }, [shopLoading, connected, loadData]);

  const openVendorChart = (vendor) => {
    setActiveVendor(vendor);
    const sys = vendorSizeSystems[vendor] || {};
    setEditSizeSystem(sys.sizeSystem || "EU");
    setEditDisplaySystem(sys.displaySystem || "EU");
    setEditChart(toEditFormat(vendorCharts[vendor]));
    setActiveGarmentTab(0);
    setModalOpen(true);
  };

  const garmentTabs = GARMENT_TYPE_LIST;
  const activeGarmentType = garmentTabs[activeGarmentTab];

  const addSizeRow = () => {
    setEditChart(prev => ({
      ...prev,
      [activeGarmentType]: [
        ...prev[activeGarmentType],
        { label: "", measurements: emptyMeasurements(activeGarmentType) },
      ],
    }));
  };

  const updateSizeLabel = (index, value) => {
    setEditChart(prev => ({
      ...prev,
      [activeGarmentType]: prev[activeGarmentType].map((row, i) => i === index ? { ...row, label: value } : row),
    }));
  };

  const updateSizeMeasurement = (index, key, value) => {
    setEditChart(prev => ({
      ...prev,
      [activeGarmentType]: prev[activeGarmentType].map((row, i) =>
        i === index ? { ...row, measurements: { ...row.measurements, [key]: Number(value) || 0 } } : row
      ),
    }));
  };

  const removeSizeRow = (index) => {
    setEditChart(prev => ({
      ...prev,
      [activeGarmentType]: prev[activeGarmentType].filter((_, i) => i !== index),
    }));
  };

  const saveVendorChart = async () => {
    if (!activeVendor) return;
    setSaving(true);
    try {
      // Only include garment types that have entries
      const chartToSave = {};
      for (const gt of GARMENT_TYPE_LIST) {
        if (editChart[gt]?.length > 0) chartToSave[gt] = editChart[gt];
      }

      await apiFetch(`/product-fitting/vendor-size-chart/${encodeURIComponent(activeVendor)}`, {
        method: "PUT",
        body: JSON.stringify({ sizeChart: chartToSave, sizeSystem: editSizeSystem, displaySystem: editDisplaySystem }),
      });
      setVendorCharts(prev => ({ ...prev, [activeVendor]: chartToSave }));
      setVendorSizeSystems(prev => ({ ...prev, [activeVendor]: { sizeSystem: editSizeSystem, displaySystem: editDisplaySystem } }));
      setSuccessMessage(`${t("sizeChart.saved") || "Maattabel opgeslagen!"} (${activeVendor})`);
      setModalOpen(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const openImport = (vendor) => {
    setImportVendor(vendor);
    const template = {
      blazer: [
        { label: "48", chest_cm: 104, waist_cm: 92, shoulders_cm: 46, sleeve_cm: 63, back_length_cm: 72, front_length_cm: 75, hem_cm: 107, sleeve_width_cm: 39, cuff_cm: 27, neck_cm: 0 },
        { label: "50", chest_cm: 108, waist_cm: 96, shoulders_cm: 48, sleeve_cm: 65, back_length_cm: 74, front_length_cm: 77, hem_cm: 111, sleeve_width_cm: 40, cuff_cm: 28, neck_cm: 0 },
      ],
      waistcoat: [
        { label: "48", chest_cm: 100, waist_cm: 92, shoulders_cm: 38, back_length_cm: 58, front_length_cm: 66, hem_cm: 100 },
        { label: "50", chest_cm: 104, waist_cm: 96, shoulders_cm: 39, back_length_cm: 60, front_length_cm: 68, hem_cm: 104 },
      ],
      trousers: [
        { label: "32", waist_cm: 86, hip_cm: 104, inseam_cm: 82, outseam_cm: 106, thigh_cm: 62, knee_cm: 46, hem_cm: 38, front_rise_cm: 26, back_rise_cm: 41 },
        { label: "34", waist_cm: 90, hip_cm: 108, inseam_cm: 82, outseam_cm: 107, thigh_cm: 65, knee_cm: 48, hem_cm: 39, front_rise_cm: 27, back_rise_cm: 43 },
      ],
    };
    setImportJson(JSON.stringify(template, null, 2));
    setImportOpen(true);
  };

  const handleImport = async () => {
    if (!importVendor) return;
    setImporting(true);
    try {
      const data = JSON.parse(importJson);
      const result = await apiFetch(`/product-fitting/vendor-size-chart/${encodeURIComponent(importVendor)}/import`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      setVendorCharts(prev => ({ ...prev, [importVendor]: result.sizeChart }));
      setSuccessMessage(`${result.sizesImported} maten geïmporteerd voor ${importVendor}!`);
      setImportOpen(false);
    } catch (err) {
      setError(`Import mislukt: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const openScrape = (vendor) => {
    setScrapeVendor(vendor);
    setScrapeUrl("");
    setScrapeOpen(true);
  };

  const handleScrape = async () => {
    if (!scrapeVendor || !scrapeUrl.trim()) return;
    setScraping(true);
    try {
      const result = await apiFetch(`/product-fitting/vendor-size-chart/${encodeURIComponent(scrapeVendor)}/scrape-url`, {
        method: "POST",
        body: JSON.stringify({ url: scrapeUrl.trim(), targetLabelSystem: editSizeSystem || "EU" }),
      });
      // Preview in de editor — merchant controleert en slaat zelf op
      setScrapeOpen(false);
      setActiveVendor(scrapeVendor);
      const sys = vendorSizeSystems[scrapeVendor] || {};
      setEditSizeSystem(sys.sizeSystem || "EU");
      setEditDisplaySystem(sys.displaySystem || "EU");
      setEditChart(toEditFormat(result.sizeChart));
      setActiveGarmentTab(0);
      setModalOpen(true);
      setSuccessMessage(
        (t("sizeChart.scrapeSuccess") || "AI heeft {count} maten van de pagina gehaald. Controleer de waarden en sla op.")
          .replace("{count}", String(result.sizesImported))
      );
    } catch (err) {
      setError(`${t("sizeChart.scrapeFailed") || "Scrapen mislukt"}: ${err.message}`);
    } finally {
      setScraping(false);
    }
  };

  const scanVendorRef = useRef(null);

  const triggerScan = (vendor) => {
    // Don't set scanning yet — only after a file is actually selected
    scanVendorRef.current = vendor;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const handleScanFile = async (e) => {
    const file = e.target.files?.[0];
    const vendor = scanVendorRef.current;
    if (!file || !vendor) {
      scanVendorRef.current = null;
      return;
    }

    // NOW show progress — file has been selected
    setScanning(vendor);
    scanVendorRef.current = null;

    // Read file as base64
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const dataUrl = reader.result;
        const base64 = dataUrl.split(",")[1];
        const mimeType = file.type || "image/jpeg";

        const result = await apiFetch(`/product-fitting/vendor-size-chart/${encodeURIComponent(vendor)}/scan`, {
          method: "POST",
          body: JSON.stringify({ imageB64: base64, mimeType }),
        });

        // Open the edit modal with the extracted data
        setVendorCharts(prev => ({ ...prev, [vendor]: result.sizeChart }));
        setActiveVendor(vendor);
        setEditChart(toEditFormat(result.sizeChart));
        setActiveGarmentTab(0);
        setModalOpen(true);
        setSuccessMessage(`AI heeft ${result.sizesImported} maten herkend uit de afbeelding voor ${vendor}. Controleer de waarden en sla op.`);
      } catch (err) {
        setError(`Scan mislukt: ${err.message}`);
      } finally {
        setScanning(null);
      }
    };
    reader.onerror = () => {
      setError("Kon afbeelding niet lezen");
      setScanning(null);
    };
    reader.readAsDataURL(file);
  };

  if (!shopLoading && !connected) {
    return (
      <Page title={t("sizeChart.pageTitle") || "Maattabellen per merk"}>
        <Layout>
          <Layout.Section>
            <Card>
              <EmptyState
                heading={t("products.notConnected")}
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              >
                <p>{t("products.notConnectedDesc")}</p>
              </EmptyState>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title={t("sizeChart.pageTitle") || "Maattabellen per merk"}>
      {/* Hidden file input for image scan */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleScanFile}
      />

      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
          </Layout.Section>
        )}
        {successMessage && (
          <Layout.Section>
            <Banner tone="success" onDismiss={() => setSuccessMessage(null)}>{successMessage}</Banner>
          </Layout.Section>
        )}

        {scanning && (
          <Layout.Section>
            <Banner tone="info">
              <BlockStack gap="200">
                <Text variant="bodyMd">AI analyseert de afbeelding voor {scanning}...</Text>
                <ProgressBar progress={75} tone="primary" />
              </BlockStack>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <Card>
            <Box padding="400">
              <BlockStack gap="400">
                <Text variant="bodyMd" tone="subdued">
                  {t("sizeChart.pageDescription") || "Stel per merk een standaard maattabel in. Alle producten van dat merk erven deze maten automatisch. Je kunt per product nog finetunen via Producten & Pasfoto's."}
                </Text>

                {loading ? (
                  <InlineStack align="center"><Spinner /></InlineStack>
                ) : vendors.length === 0 ? (
                  <Text variant="bodyMd" tone="subdued">
                    {t("sizeChart.noVendors") || "Geen merken gevonden. Producten worden geladen vanuit Shopify."}
                  </Text>
                ) : (
                  <BlockStack gap="300">
                    {vendors.map(vendor => {
                      const chart = vendorCharts[vendor];
                      const hasChart = chart && (
                        Array.isArray(chart) ? chart.length > 0
                          : GARMENT_TYPE_LIST.some(gt => chart[gt]?.length > 0)
                      );
                      const sizeCount = Array.isArray(chart) ? chart.length
                        : GARMENT_TYPE_LIST.reduce((sum, gt) => sum + (chart?.[gt]?.length || 0), 0);
                      return (
                        <div
                          key={vendor}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "12px 16px",
                            background: hasChart ? "#f0fdf4" : "#fafafa",
                            borderRadius: 10,
                            border: hasChart ? "1px solid #bbf7d0" : "1px solid #e5e5e5",
                          }}
                        >
                          <InlineStack gap="300" blockAlign="center">
                            <Text variant="bodyMd" fontWeight="bold">{vendor}</Text>
                            {hasChart ? (
                              <Badge tone="success">{sizeCount} {t("sizeChart.sizes") || "maten"}</Badge>
                            ) : (
                              <Badge tone="attention">{t("sizeChart.noChart") || "Geen maattabel"}</Badge>
                            )}
                            {hasChart && vendorSizeSystems[vendor] && vendorSizeSystems[vendor].sizeSystem !== vendorSizeSystems[vendor].displaySystem && (
                              <Badge tone="info">{vendorSizeSystems[vendor].sizeSystem} → {vendorSizeSystems[vendor].displaySystem}</Badge>
                            )}
                          </InlineStack>
                          <InlineStack gap="200">
                            <Button
                              onClick={() => triggerScan(vendor)}
                              size="slim"
                              variant="plain"
                              loading={scanning === vendor}
                            >
                              {scanning === vendor
                                ? (t("sizeChart.scanning") || "Analyseren...")
                                : (t("sizeChart.scanImage") || "📷 Scan afbeelding")}
                            </Button>
                            <Button onClick={() => openScrape(vendor)} size="slim" variant="plain">
                              {t("sizeChart.scrapeUrl") || "🌐 Scrape URL"}
                            </Button>
                            <Button onClick={() => openImport(vendor)} size="slim" variant="plain">
                              {t("sizeChart.import") || "Importeren"}
                            </Button>
                            <Button onClick={() => openVendorChart(vendor)} size="slim">
                              {hasChart
                                ? (t("sizeChart.editChart") || "Bewerken")
                                : (t("sizeChart.addChart") || "Maattabel toevoegen")}
                            </Button>
                          </InlineStack>
                        </div>
                      );
                    })}
                  </BlockStack>
                )}
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Vendor size chart modal */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`${t("sizeChart.title") || "Maattabel"} — ${activeVendor || ""}`}
        primaryAction={{
          content: saving ? t("common.saving") : t("common.save"),
          onAction: saveVendorChart,
          loading: saving,
        }}
        secondaryActions={[
          { content: t("common.cancel"), onAction: () => setModalOpen(false) },
        ]}
        large
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text variant="bodyMd" tone="subdued">
              {t("sizeChart.vendorDescription") || "Deze maten gelden voor alle producten van dit merk, tenzij je per product een eigen maattabel instelt."}
            </Text>

            {/* Size system conversion settings */}
            <div style={{
              display: "flex",
              gap: 16,
              padding: "12px 16px",
              background: "#f8f9fa",
              borderRadius: 10,
              border: "1px solid #e5e5e5",
              alignItems: "center",
              flexWrap: "wrap",
            }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
                  Maatvoering in tabel
                </label>
                <select
                  value={editSizeSystem}
                  onChange={e => setEditSizeSystem(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, background: "#fff" }}
                >
                  <option value="EU">EU (46, 48, 50...)</option>
                  <option value="UK">UK (36, 38, 40...)</option>
                  <option value="US">US (36, 38, 40...)</option>
                  <option value="IT">IT (46, 48, 50...)</option>
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", paddingTop: 18, fontSize: 18, color: "#888" }}>→</div>
              <div style={{ flex: 1, minWidth: 180 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4 }}>
                  Tonen aan klant als
                </label>
                <select
                  value={editDisplaySystem}
                  onChange={e => setEditDisplaySystem(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ccc", fontSize: 13, background: "#fff" }}
                >
                  <option value="EU">EU (46, 48, 50...)</option>
                  <option value="UK">UK (36, 38, 40...)</option>
                  <option value="US">US (36, 38, 40...)</option>
                  <option value="IT">IT (46, 48, 50...)</option>
                </select>
              </div>
              {editSizeSystem !== editDisplaySystem && (
                <div style={{ width: "100%", fontSize: 12, color: "#6366f1", fontWeight: 500, padding: "4px 0 0" }}>
                  ℹ️ Maten worden automatisch omgerekend van {editSizeSystem} naar {editDisplaySystem} bij het tonen aan de klant.
                </div>
              )}
            </div>

            <Tabs
              tabs={garmentTabs.map((gt, i) => ({
                id: gt,
                content: `${GARMENT_LABELS[gt]} (${editChart[gt]?.length || 0})`,
              }))}
              selected={activeGarmentTab}
              onSelect={setActiveGarmentTab}
            />

            {(() => {
              const rows = editChart[activeGarmentType] || [];
              const keys = GARMENT_KEYS[activeGarmentType] || ALL_MEASUREMENT_KEYS;
              return (
                <>
                  {rows.length > 0 && (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: "2px solid #e1e3e5" }}>
                            <th style={{ padding: "8px 6px", textAlign: "left", minWidth: 80 }}>{t("sizeChart.sizeLabel") || "Maat"}</th>
                            {keys.map(key => (
                              <th key={key} style={{ padding: "8px 6px", textAlign: "center", minWidth: 70 }}>{MEASUREMENT_LABELS[key]}</th>
                            ))}
                            <th style={{ padding: "8px 6px", width: 40 }}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, idx) => (
                            <tr key={idx} style={{ borderBottom: "1px solid #f1f1f1" }}>
                              <td style={{ padding: "4px 2px" }}>
                                <input
                                  type="text"
                                  value={row.label}
                                  onChange={e => updateSizeLabel(idx, e.target.value)}
                                  placeholder={activeGarmentType === "pantalon" ? "34" : "50"}
                                  style={{ width: "100%", padding: "6px 8px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13 }}
                                />
                              </td>
                              {keys.map(key => (
                                <td key={key} style={{ padding: "4px 2px" }}>
                                  <input
                                    type="number"
                                    min="0"
                                    value={row.measurements[key] || ""}
                                    onChange={e => updateSizeMeasurement(idx, key, e.target.value)}
                                    placeholder="0"
                                    style={{ width: "100%", padding: "6px 4px", border: "1px solid #ccc", borderRadius: 6, fontSize: 13, textAlign: "center" }}
                                  />
                                </td>
                              ))}
                              <td style={{ padding: "4px 2px", textAlign: "center" }}>
                                <button
                                  onClick={() => removeSizeRow(idx)}
                                  style={{ background: "none", border: "none", color: "#d72c0d", cursor: "pointer", fontSize: 16 }}
                                  title={t("common.delete")}
                                >
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {rows.length === 0 && (
                    <Box padding="400" background="bg-surface-secondary" borderRadius="200">
                      <Text variant="bodyMd" tone="subdued" alignment="center">
                        Nog geen maten voor {GARMENT_LABELS[activeGarmentType]}. Klik op "+ Maat toevoegen" om te beginnen.
                      </Text>
                    </Box>
                  )}
                </>
              );
            })()}

            <Button onClick={addSizeRow}>
              {t("sizeChart.addSize") || "+ Maat toevoegen"}
            </Button>
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Import from manufacturer specs modal */}
      <Modal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={`${t("sizeChart.import") || "Importeren"} — ${importVendor || ""}`}
        primaryAction={{
          content: importing ? "Importeren..." : (t("sizeChart.importButton") || "Importeren & converteren"),
          onAction: handleImport,
          loading: importing,
        }}
        secondaryActions={[
          { content: t("common.cancel"), onAction: () => setImportOpen(false) },
        ]}
        large
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text variant="bodyMd" tone="subdued">
              Plak de fabrikantspecificaties in JSON-formaat. Alle maten in cm (volle omtrek voor rondmaten). Het systeem converteert automatisch naar lichaamsafmetingen.
            </Text>
            <Banner tone="info">
              <p><strong>Colbert:</strong> chest_cm, waist_cm, shoulders_cm, sleeve_cm, back_length_cm, front_length_cm, hem_cm, sleeve_width_cm, cuff_cm, neck_cm</p>
              <p><strong>Gilet:</strong> chest_cm, waist_cm, shoulders_cm, back_length_cm, front_length_cm, hem_cm</p>
              <p><strong>Pantalon:</strong> waist_cm, hip_cm, inseam_cm, outseam_cm, thigh_cm, knee_cm, hem_cm, front_rise_cm, back_rise_cm</p>
              <p><strong>Ease:</strong> Ruimte die het kledingstuk groter is dan het lichaam (standaard al ingevuld)</p>
            </Banner>
            <textarea
              value={importJson}
              onChange={e => setImportJson(e.target.value)}
              style={{
                width: "100%", minHeight: 300, fontFamily: "monospace", fontSize: 12,
                padding: 12, border: "1px solid #ccc", borderRadius: 8,
                whiteSpace: "pre", overflowX: "auto",
              }}
            />
          </BlockStack>
        </Modal.Section>
      </Modal>

      {/* Scrape size guide from URL modal */}
      <Modal
        open={scrapeOpen}
        onClose={() => setScrapeOpen(false)}
        title={`${t("sizeChart.scrapeUrl") || "🌐 Scrape URL"} — ${scrapeVendor || ""}`}
        primaryAction={{
          content: scraping ? (t("sizeChart.scraping") || "Pagina analyseren...") : (t("sizeChart.scrapeButton") || "Scrape & converteer"),
          onAction: handleScrape,
          loading: scraping,
          disabled: !scrapeUrl.trim(),
        }}
        secondaryActions={[
          { content: t("common.cancel"), onAction: () => setScrapeOpen(false) },
        ]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            <Text variant="bodyMd" tone="subdued">
              {t("sizeChart.scrapeDescription") || "Plak de URL van de size-guide pagina van het merk. De AI leest de maattabellen, rekent inches en UK-maten om naar cm/EU en converteert kledingmaten naar lichaamsmaten. Je krijgt eerst een preview — opslaan doe je zelf."}
            </Text>
            <TextField
              label="URL"
              value={scrapeUrl}
              onChange={setScrapeUrl}
              placeholder="https://cavani.co.uk/pages/size-guide"
              autoComplete="off"
              type="url"
            />
            {scraping && (
              <BlockStack gap="200">
                <Text variant="bodyMd" tone="subdued">{t("sizeChart.scraping") || "Pagina analyseren..."}</Text>
                <ProgressBar progress={75} tone="primary" />
              </BlockStack>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
